import { supabase } from '@/integrations/supabase/client';

export const REFERRAL_STORAGE_KEY = 'repasogpc_referral_code';
export const REFERRAL_CLAIMED_EVENT = 'repasogpc:referral-claimed';

export interface ReferralCheckoutBenefit {
  eligible: boolean;
  reason?: string;
  referral_id?: string;
  affiliate_id?: string;
  code?: string;
  discount_percent?: number;
  discount_duration?: 'forever';
  trial_days?: number;
}

export function isValidClabe(value: string): boolean {
  const clabe = value.replace(/\s/g, '');
  if (!/^\d{18}$/.test(clabe)) return false;

  const weights = [3, 7, 1];
  const sum = clabe
    .slice(0, 17)
    .split('')
    .reduce((total, digit, index) => total + ((Number(digit) * weights[index % 3]) % 10), 0);
  const expectedCheckDigit = (10 - (sum % 10)) % 10;
  return expectedCheckDigit === Number(clabe[17]);
}

/**
 * Builds the canonical ambassador link. It opens the real signup screen and
 * lets URLSearchParams safely encode any characters present in the code.
 */
export function buildAffiliateInviteUrl(origin: string, code: string): string {
  const url = new URL('/auth', origin);
  url.searchParams.set('ref', code.trim());
  return url.toString();
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return 'unknown_error';
}

export interface AffiliateSummary {
  is_affiliate: boolean;
  id?: string;
  code?: string;
  discount_percent?: number;
  initial_commission_percent?: number;
  recurring_commission_percent?: number;
  payout_info?: {
    bank_name?: string;
    clabe?: string;
    account_holder?: string;
  };
  status?: 'active' | 'paused' | 'suspended';
  total_earned: number;
  pending_payout: number;
  pending_reversals?: number;
  total_referrals: number;
  converted_referrals: number;
  active_subscribers: number;
  recent_commissions: Array<{
    id: string;
    type: 'initial' | 'recurring';
    sale_amount: number;
    commission_rate: number;
    commission_amount: number;
    reversed_amount?: number;
    net_commission_amount?: number;
    currency: string;
    status: 'pending' | 'paid' | 'cancelled';
    created_at: string;
    referred_name?: string | null;
  }>;
}

/**
 * Reads any ?ref=, ?promo= or ?aff= parameter from the current URL and persists it in localStorage.
 */
export function captureReferralCodeFromUrl(search = window.location.search): string | null {
  try {
    const params = new URLSearchParams(search);
    const code = params.get('ref') || params.get('promo') || params.get('aff') || params.get('codigo');
    if (code && code.trim().length >= 2) {
      const sanitized = code.trim().toUpperCase();
      localStorage.setItem(REFERRAL_STORAGE_KEY, sanitized);
      return sanitized;
    }
  } catch (err) {
    console.warn('[Affiliates] Failed to capture referral from URL:', err);
  }
  return null;
}

/**
 * Returns any stored referral code from localStorage.
 */
export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears stored referral code from localStorage.
 */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Claims the referral code for the currently logged in user in Supabase.
 */
export async function claimStoredReferralCode(): Promise<{ success: boolean; code?: string; error?: string }> {
  const code = getStoredReferralCode();
  if (!code) return { success: false, error: 'no_code_stored' };

  try {
    const { data, error } = await supabase.rpc('claim_referral_code', { p_code: code });
    if (error) throw error;
    const result = (data ?? {}) as { success?: boolean; code?: string; error?: string };
    if (result.success) {
      clearStoredReferralCode();
      window.dispatchEvent(new CustomEvent(REFERRAL_CLAIMED_EVENT));
      return { success: true, code: result.code };
    }
    return { success: false, error: result.error || 'claim_failed' };
  } catch (err: unknown) {
    console.warn('[Affiliates] Error claiming referral code:', err);
    return { success: false, error: getErrorMessage(err) };
  }
}

/**
 * Returns the server-authoritative checkout benefit for the current user.
 */
export async function fetchReferralCheckoutBenefit(): Promise<ReferralCheckoutBenefit | null> {
  const { data, error } = await supabase.rpc('get_referral_checkout_benefit');
  if (error) throw error;

  const benefit = (data ?? {}) as ReferralCheckoutBenefit;
  if (typeof benefit.eligible !== 'boolean') {
    throw new Error('Invalid referral benefit response');
  }
  if (!benefit.eligible) return null;

  const discountPercent = Number(benefit.discount_percent);
  const trialDays = Number(benefit.trial_days ?? 0);
  if (
    typeof benefit.code !== 'string'
    || !benefit.code
    || !Number.isFinite(discountPercent)
    || discountPercent <= 0
    || discountPercent > 100
    || !Number.isInteger(trialDays)
    || trialDays < 0
  ) {
    throw new Error('Invalid referral benefit configuration');
  }

  return {
    ...benefit,
    discount_percent: discountPercent,
    trial_days: trialDays,
  };
}

/**
 * Fetches the affiliate summary for the logged-in user.
 */
export async function fetchAffiliateSummary(): Promise<AffiliateSummary> {
  const { data, error } = await supabase.rpc('get_affiliate_summary');
  if (error) {
    console.error('[Affiliates] Error fetching affiliate summary:', error);
    return {
      is_affiliate: false,
      total_earned: 0,
      pending_payout: 0,
      total_referrals: 0,
      converted_referrals: 0,
      active_subscribers: 0,
      recent_commissions: [],
    };
  }
  return data as unknown as AffiliateSummary;
}

/**
 * Updates payout information (CLABE, Bank, Name) for the affiliate.
 */
export async function updateAffiliatePayoutInfo(
  _affiliateId: string,
  payoutInfo: { bank_name: string; clabe: string; account_holder: string }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('update_affiliate_payout_info', {
      p_payout_info: payoutInfo
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'error' in data) {
      return { success: false, error: String((data as { error: string }).error) };
    }
    return { success: true };
  } catch (err: unknown) {
    return { success: false, error: getErrorMessage(err) };
  }
}
